import { FC } from 'react';
import { useTranslation } from 'next-i18next';
import {
  Avatar,
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  useTheme,
  styled,
  alpha,
  ListItemProps,
  SxProps
} from '@mui/material';
import { Workspace } from '@guud/creative-studio-types';
import { capitalizeInitials } from '@/common/utils/capitalizedInitials';
import { parseCurrentWorkspaceName } from '@/common/utils/parseWorkspaceName';
import { useUserStore } from '@/user/stores/userStore';
import DoneIcon from '@mui/icons-material/Done';
import UnfoldMoreTwoToneIcon from '@mui/icons-material/UnfoldMoreTwoTone';

interface WorkspaceListItemProps {
  workspace: Workspace;
  onClick(): void;
  showCurrent?: boolean;
  showCheck?: boolean;
  showUnfold?: boolean;
  size?: 'medium' | 'large';
  styles?: SxProps;
}

const LabelWrapper = styled(Box)(({ theme }) => ({
  fontSize: theme.typography.pxToRem(9),
  fontWeight: 'bold',
  textTransform: 'uppercase',
  borderRadius: theme.general.borderRadiusSm,
  padding: theme.spacing(0.5, 0.8),
  lineHeight: 1
}));

export const WorkspaceListItem: FC<WorkspaceListItemProps & ListItemProps> = (
  props
) => {
  const {
    onClick,
    workspace,
    showCurrent,
    showCheck,
    showUnfold,
    size = 'medium',
    styles
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { currentWorkspace } = useUserStore();
  const avatarSize = {
    medium: { width: 42, height: 42 },
    large: { width: 54, height: 54 }
  };

  return (
    <ListItem
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: `${showCurrent ? 'none' : 'space-between'}`,
        width: `${showCurrent ? 'fit-content' : '100%'}`,
        p: 0,
        ...styles
      }}
    >
      <ListItemAvatar
        sx={{
          mr: 1,
          display: 'flex',
          alignItems: 'center',
          minWidth: 0
        }}
      >
        <Avatar
          sx={{
            background: `${
              workspace.id === currentWorkspace
                ? theme.colors.primary.main
                : theme.colors.secondary.main
            }`,
            width: avatarSize[size].width,
            height: avatarSize[size].height
          }}
          variant={'rounded'}
        >
          {capitalizeInitials(workspace.name)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography
            gutterBottom
            component="span"
            noWrap
            sx={{
              display: 'flex',
              alignItems: 'center',
              m: 0,
              fontSize: theme.typography.pxToRem(size === 'medium' ? 12 : 14),
              fontWeight: theme.typography.fontWeightBold,
              color: theme.colors.alpha.black[100]
            }}
          >
            {parseCurrentWorkspaceName(workspace.name)}
            {showCurrent && currentWorkspace === workspace.id && (
              <LabelWrapper
                ml={0.7}
                component="span"
                sx={{
                  background: alpha(theme.colors.info.light, 0.2),
                  color: theme.colors.info.main
                }}
              >
                {t('Current')}
              </LabelWrapper>
            )}
          </Typography>
        }
        secondary={
          <Typography
            noWrap
            variant="subtitle2"
            sx={{
              fontSize: theme.typography.pxToRem(size === 'medium' ? 12 : 14)
            }}
          >
            {t('1 member')}
          </Typography>
        }
      />
      {showCheck && currentWorkspace === workspace.id && (
        <DoneIcon sx={{ color: theme.colors.primary.main }} />
      )}
      {showUnfold && (
        <UnfoldMoreTwoToneIcon
          sx={{
            color: theme.colors.primary.main
          }}
        />
      )}
    </ListItem>
  );
};
